import React, { useState, useEffect } from "react";
import { db } from "../../services/firebase";
import {
	collection,
	onSnapshot,
	doc,
	updateDoc,
	query,
	orderBy,
} from "firebase/firestore";
import {
	ChevronDown,
	ChevronUp,
	Package,
	Trash2,
	Download,
	ShoppingBag,
} from "lucide-react";
import MerchProductManager from "./MerchProductManager";

const STATUS_OPTIONS = ["pending", "confirmed", "collected", "cancelled"];

const STATUS_COLORS = {
	pending: "bg-warning text-dark",
	confirmed: "bg-info",
	collected: "bg-success",
	cancelled: "bg-danger",
};

const MerchOrders = ({ loading: parentLoading }) => {
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [expandedOrders, setExpandedOrders] = useState({});
	const [filterStatus, setFilterStatus] = useState("all");
	const [activeTab, setActiveTab] = useState("orders");

	useEffect(() => {
		const q = query(
			collection(db, "merchOrders"),
			orderBy("createdAt", "desc"),
		);
		const unsubscribe = onSnapshot(q, (snapshot) => {
			const ordersData = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setOrders(ordersData);
			setLoading(false);
		});
		return () => unsubscribe();
	}, []);

	const toggleOrder = (orderId) => {
		setExpandedOrders((prev) => ({
			...prev,
			[orderId]: !prev[orderId],
		}));
	};

	const updateOrderStatus = async (orderId, newStatus) => {
		try {
			await updateDoc(doc(db, "merchOrders", orderId), {
				status: newStatus,
			});
		} catch (error) {
			console.error("Error updating order status:", error);
		}
	};

	const exportOrdersCSV = () => {
		const escapeCSV = (val) => {
			const str = String(val ?? "");
			return str.includes(",") || str.includes('"') || str.includes("\n")
				? `"${str.replace(/"/g, '""')}"`
				: str;
		};
		const headers = [
			"Date",
			"First Name",
			"Last Name",
			"Email",
			"Phone",
			"Items",
			"Total",
			"Status",
			"Notes",
		];
		const rows = filteredOrders.map((o) => [
			new Date(o.createdAt).toLocaleDateString(),
			o.firstName,
			o.lastName,
			o.email,
			o.phone,
			(o.items || [])
				.map((i) => `${i.name} (${i.size}) x${i.quantity}`)
				.join("; "),
			`£${(o.total || 0).toFixed(2)}`,
			o.status,
			o.notes || "",
		]);
		const csv = [headers, ...rows]
			.map((r) => r.map(escapeCSV).join(","))
			.join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `merch-orders-${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const filteredOrders =
		filterStatus === "all"
			? orders
			: orders.filter((o) => o.status === filterStatus);

	const totalRevenue = orders
		.filter((o) => o.status !== "cancelled")
		.reduce((sum, o) => sum + (o.total || 0), 0);

	const pendingCount = orders.filter((o) => o.status === "pending").length;

	if (loading || parentLoading) {
		return (
			<div className="card">
				<div className="card-body text-center py-5">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="card">
			<div className="card-header">
				<h5 className="mb-3" style={{ fontSize: "1.1rem" }}>
					<Package className="me-2" size={20} />
					Merchandise
				</h5>
				{/* Tabs */}
				<ul
					className="nav nav-tabs"
					style={{
						flexWrap: "nowrap",
						borderBottom: "none",
						marginBottom: 0,
						gap: "0.5rem",
					}}
				>
					<li className="nav-item" style={{ flex: "1 1 0" }}>
						<button
							className={`nav-link ${activeTab === "orders" ? "active" : ""} w-100 text-center`}
							onClick={() => setActiveTab("orders")}
							style={
								activeTab === "orders"
									? {
											backgroundColor: "#d15a36",
											borderColor: "#d15a36",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
									: {
											backgroundColor: "rgba(209, 90, 54, 0.15)",
											borderColor: "transparent",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
							}
						>
							<Package size={14} className="me-1" />
							Orders
							{pendingCount > 0 && (
								<span
									className="badge bg-warning text-dark ms-1"
									style={{ fontSize: "0.65rem" }}
								>
									{pendingCount}
								</span>
							)}
						</button>
					</li>
					<li className="nav-item" style={{ flex: "1 1 0" }}>
						<button
							className={`nav-link ${activeTab === "products" ? "active" : ""} w-100 text-center`}
							onClick={() => setActiveTab("products")}
							style={
								activeTab === "products"
									? {
											backgroundColor: "#d15a36",
											borderColor: "#d15a36",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
									: {
											backgroundColor: "rgba(209, 90, 54, 0.15)",
											borderColor: "transparent",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
							}
						>
							<ShoppingBag size={14} className="me-1" />
							Products
						</button>
					</li>
				</ul>
			</div>

			<div className="card-body">
				{activeTab === "products" && <MerchProductManager />}

				{activeTab === "orders" && (
					<>
						{/* Filter & Export controls */}
						<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
							<div className="d-flex align-items-center gap-2">
								{pendingCount > 0 && (
									<span className="badge bg-warning text-dark">
										{pendingCount} pending
									</span>
								)}
								<select
									className="form-select form-select-sm"
									style={{ width: "auto" }}
									value={filterStatus}
									onChange={(e) => setFilterStatus(e.target.value)}
								>
									<option value="all">All Orders</option>
									{STATUS_OPTIONS.map((s) => (
										<option key={s} value={s}>
											{s.charAt(0).toUpperCase() + s.slice(1)}
										</option>
									))}
								</select>
							</div>
							<button
								className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
								onClick={exportOrdersCSV}
								disabled={filteredOrders.length === 0}
								title="Export to CSV"
							>
								<Download size={16} />
								Export
							</button>
						</div>

						{/* Revenue summary */}
						<div className="d-flex gap-3 mb-3 flex-wrap">
							<div
								className="p-2 bg-light rounded text-center"
								style={{ minWidth: 120 }}
							>
								<small className="text-muted d-block">Total Orders</small>
								<strong>{orders.length}</strong>
							</div>
							<div
								className="p-2 bg-light rounded text-center"
								style={{ minWidth: 120 }}
							>
								<small className="text-muted d-block">Revenue</small>
								<strong>£{totalRevenue.toFixed(2)}</strong>
							</div>
							<div
								className="p-2 bg-light rounded text-center"
								style={{ minWidth: 120 }}
							>
								<small className="text-muted d-block">Pending</small>
								<strong>{pendingCount}</strong>
							</div>
						</div>

						{filteredOrders.length === 0 ? (
							<div className="text-center py-5">
								<p className="text-muted">
									{filterStatus === "all"
										? "No merchandise orders yet."
										: `No ${filterStatus} orders.`}
								</p>
							</div>
						) : (
							<div className="merch-orders-list">
								{filteredOrders.map((order) => {
									const isExpanded = expandedOrders[order.id];
									return (
										<div key={order.id} className="notification-card mb-3">
											<div
												className="notification-header"
												onClick={() => toggleOrder(order.id)}
												style={{ cursor: "pointer" }}
											>
												<div className="d-flex justify-content-between align-items-center">
													<div className="d-flex align-items-center gap-2 flex-grow-1">
														<span
															className={`badge ${STATUS_COLORS[order.status] || "bg-secondary"}`}
														>
															{order.status}
														</span>
														<h6 className="mb-0">
															{order.firstName} {order.lastName}
														</h6>
														<small className="text-muted">
															£{(order.total || 0).toFixed(2)}
														</small>
													</div>
													<div className="d-flex align-items-center gap-2">
														<small className="text-muted">
															{new Date(order.createdAt).toLocaleDateString()}
														</small>
														{isExpanded ? (
															<ChevronUp size={20} />
														) : (
															<ChevronDown size={20} />
														)}
													</div>
												</div>
											</div>

											{isExpanded && (
												<div className="notification-body mt-3">
													<div className="mb-2">
														<small className="text-muted">Email:</small>{" "}
														{order.email}
													</div>
													<div className="mb-2">
														<small className="text-muted">Phone:</small>{" "}
														{order.phone}
													</div>
													{order.notes && (
														<div className="mb-2">
															<small className="text-muted">Notes:</small>{" "}
															{order.notes}
														</div>
													)}
													<div className="table-responsive mt-2">
														<table className="table table-sm">
															<thead>
																<tr>
																	<th>Item</th>
																	<th>Size</th>
																	<th>Qty</th>
																	<th>Price</th>
																</tr>
															</thead>
															<tbody>
																{(order.items || []).map((item, i) => (
																	<tr key={i}>
																		<td>{item.name}</td>
																		<td>{item.size}</td>
																		<td>{item.quantity}</td>
																		<td>
																			£{(item.price * item.quantity).toFixed(2)}
																		</td>
																	</tr>
																))}
															</tbody>
															<tfoot>
																<tr>
																	<td colSpan="3" className="text-end fw-bold">
																		Total
																	</td>
																	<td className="fw-bold">
																		£{(order.total || 0).toFixed(2)}
																	</td>
																</tr>
															</tfoot>
														</table>
													</div>
													<div className="d-flex align-items-center gap-2 mt-2">
														<label className="form-label mb-0 me-1">
															<small>Status:</small>
														</label>
														<select
															className="form-select form-select-sm"
															style={{ width: "auto" }}
															value={order.status}
															onChange={(e) =>
																updateOrderStatus(order.id, e.target.value)
															}
														>
															{STATUS_OPTIONS.map((s) => (
																<option key={s} value={s}>
																	{s.charAt(0).toUpperCase() + s.slice(1)}
																</option>
															))}
														</select>
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default MerchOrders;
